import { useEffect, useRef, useState } from 'react'
import { MapPin } from 'lucide-react'
import { CountUp } from '../../ui/primitives'
import { VehicleSearchField } from '../VehicleSearchField'
import {
  reportInput, reportBtnPrimary, reportBtnGhost, reportPill,
  reportTableScroll, reportTableWrap, reportTd, reportTh, reportThead, reportTr, rowEntrance,
  defaultOneDayRange,
} from '../ui'

interface GeoTag {
  ts: string
  vehicle_id: string
  driver_name: string
  site: string | null
  event: string
  geofence_id: string
  geofence_name: string
  fence_type: string | null
  hub_code: string | null
  route_id: string | null
  load_state: string | null
  trip_boundary: boolean | null
  dwell_sec: number | null
  soc_in: number | null
  soc_out: number | null
  lat: number | null
  lon: number | null
  round_trip_id: string | null
}

// f_geotags_clean's shape: one row per DWELL, not per crossing -- ts_in/ts_out/raw_crossings
// replace ts/event/lat/lon, which stop being meaningful once several crossings are merged.
interface CleanTag {
  vehicle_id: string
  geofence_id: string
  geofence_name: string
  fence_type: string | null
  hub_code: string | null
  site: string | null
  route_id: string | null
  load_state: string | null
  round_trip_id: string | null
  driver_name: string
  ts_in: string
  ts_out: string
  dwell_sec: number | null
  raw_crossings: number
  soc_in: number | null
  soc_out: number | null
}

interface DataRange {
  earliest: string | null
  latest: string | null
}

// DHAR / JHUL / DHULE -- geofence_hit.site exactly, not vehicle.depot. 67 of 70 vehicles
// carry the identical "Dhule|Julwania|Dhar" depot (a fleet assignment, not where a truck
// actually was on a given day), so depot is useless as a filter here.
const SITES = ['DHAR', 'JHUL', 'DHULE'] as const

function eventPill(event: string): JSX.Element {
  const tone = /enter/i.test(event) ? 'ok' as const : /exit/i.test(event) ? 'info' as const : 'neutral' as const
  return <span className={reportPill(tone)}>{event.toLowerCase()}</span>
}

function SkeletonTagRows({ cols }: { cols: number }): JSX.Element {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <tr key={i} className="border-b border-slate-100 last:border-b-0" style={{ opacity: 1 - i * 0.09 }}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className={reportTd}>
              <div className="skeleton h-3.5" style={{ width: j === 0 ? '6.5rem' : '4rem' }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

function EmptyTableRow({ cols, children }: { cols: number; children: string }): JSX.Element {
  return (
    <tr>
      <td colSpan={cols} className="px-3 py-12">
        <div className="flex animate-fade-in flex-col items-center gap-2 text-center">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <MapPin size={16} />
          </span>
          <span className="text-[12.5px] font-medium text-slate-600">{children}</span>
          <span className="text-[11.5px] text-slate-400">Widen the window or clear the vehicle filter.</span>
        </div>
      </td>
    </tr>
  )
}

export function GeoTagsReport(): JSX.Element {
  const [vehicleId, setVehicleId] = useState('')
  const [site, setSite] = useState('')
  const [dataRange, setDataRange] = useState<DataRange | null>(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [clean, setClean] = useState(false)
  const [rows, setRows] = useState<GeoTag[] | null>(null)
  const [cleanRows, setCleanRows] = useState<CleanTag[] | null>(null)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A later filter change must win over an earlier in-flight fetch, now that vehicle/site
  // changes trigger a load too instead of only ever firing from a direct user click.
  const requestIdRef = useRef(0)

  // The record's own earliest/latest, same reasoning as RoundTripJourney's DataWindow: a
  // picker with no floor lets someone select a date nothing was ever collected on.
  useEffect(() => {
    let live = true
    fetch('/api/reports/data-range')
      .then((r) => (r.ok ? (r.json() as Promise<DataRange>) : null))
      .then((d) => {
        if (!live || !d?.earliest || !d?.latest) return
        setDataRange(d)
        const { from: f, to: t } = defaultOneDayRange(d)
        setFrom(f)
        setTo(t)
      })
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  const load = async (): Promise<void> => {
    const myRequestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: '5000' })
      if (vehicleId) params.set('vehicle_id', vehicleId)
      if (site) params.set('site', site)
      if (from) params.set('from', from)
      if (to) params.set('to', `${to}T23:59:59`)
      if (clean) params.set('clean', 'true')
      const r = await fetch(`/api/reports/geotags?${params}`)
      const body = (await r.json()) as {
        rows?: (GeoTag | CleanTag)[]; count?: number; error?: string
      }
      if (!r.ok || body.error) throw new Error(body.error ?? String(r.status))
      if (myRequestId !== requestIdRef.current) return // superseded by a later filter change
      if (clean) {
        setCleanRows((body.rows ?? []) as CleanTag[])
        setRows(null)
      } else {
        setRows((body.rows ?? []) as GeoTag[])
        setCleanRows(null)
      }
      setCount(body.count ?? 0)
    } catch (e) {
      if (myRequestId !== requestIdRef.current) return
      setError(e instanceof Error ? e.message : String(e))
      setRows(null)
      setCleanRows(null)
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false)
    }
  }

  // First load once the record's own bounds resolve, then again on any filter change --
  // debounced, so typing into the vehicle field doesn't fire one fetch per keystroke, but
  // vehicle/site changes no longer need a date change (or the apply button) to take effect.
  useEffect(() => {
    if (!from || !to) return undefined
    const id = setTimeout(() => void load(), 350)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, site, from, to, clean])

  const downloadHref = (): string => {
    const params = new URLSearchParams()
    if (vehicleId) params.set('vehicle_id', vehicleId)
    if (site) params.set('site', site)
    if (from) params.set('from', from)
    if (to) params.set('to', `${to}T23:59:59`)
    if (clean) params.set('clean', 'true')
    return `/api/reports/geotags.csv?${params}`
  }

  const min = dataRange?.earliest?.slice(0, 10)
  const max = dataRange?.latest?.slice(0, 10)
  const hasData = rows != null || cleanRows != null

  return (
    <div className="mx-auto max-w-6xl p-4">
      <h1 className="text-[15px] font-semibold text-slate-900">Geo tags</h1>
      <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500">
        Every geofence crossing for one vehicle, one window — the raw crossing record, not
        folded into a hub visit or a leg. round_trip_id is the durable geotag-derived one, the
        same fix this session already made everywhere else it appears. Tick "clean" to collapse
        GPS boundary jitter — consecutive ENTER/EXIT pairs at the same fence under 5 minutes
        apart — into one dwell per stop.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-[rgba(15,23,42,0.07)] bg-white/70 p-3.5 shadow-sm">
        <VehicleSearchField value={vehicleId} onChange={setVehicleId} />
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          depot / site
          <select value={site} onChange={(e) => setSite(e.target.value)} className={reportInput}>
            <option value="">all sites</option>
            {SITES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          from
          <input
            type="date" value={from} min={min} max={to || max}
            onChange={(e) => {
              const next = e.target.value
              setFrom(next)
              if (to && next > to) setTo(next)
            }}
            className={`${reportInput} font-mono`}
          />
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          to
          <input
            type="date" value={to} min={from || min} max={max}
            onChange={(e) => {
              const next = e.target.value
              setTo(next)
              if (from && next < from) setFrom(next)
            }}
            className={`${reportInput} font-mono`}
          />
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 pb-1.5 text-[12px] text-slate-600">
          <input
            type="checkbox" checked={clean}
            onChange={(e) => setClean(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
          />
          clean (collapse same-fence jitter into one dwell)
        </label>
        <button
          type="button" onClick={() => void load()} disabled={loading}
          className={`${reportBtnPrimary} h-[30px]`}
        >
          {loading ? 'loading…' : 'apply'}
        </button>
        <a href={downloadHref()} className={`${reportBtnGhost} h-[30px]`}>
          Download CSV
        </a>
      </div>

      {error ? (
        <div className="mt-3 animate-slide-up rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-3 font-mono text-[11px] tabular-nums text-slate-400">
        {hasData ? (
          <>
            <CountUp value={count} /> {clean ? 'dwell' : 'tag'}{count === 1 ? '' : 's'}
            {count >= 5000 ? ' (capped at 5,000 on screen — the CSV download has no such cap)' : ''}
          </>
        ) : null}
        {min && max ? ` · record runs ${min} → ${max}` : ''}
      </div>

      <div className={`mt-2 ${reportTableWrap}`}>
        <div className={`${reportTableScroll} transition-opacity duration-200 ${loading && hasData ? 'opacity-60' : ''}`}>
        {clean ? (
          <table className="w-full text-left text-[11.5px]" aria-busy={loading || undefined}>
            <thead className={reportThead}>
              <tr>
                <th className={reportTh}>in</th>
                <th className={reportTh}>out</th>
                <th className={reportTh}>vehicle</th>
                <th className={reportTh}>site</th>
                <th className={reportTh}>geofence</th>
                <th className={reportTh}>type</th>
                <th className={`${reportTh} text-right`}>dwell</th>
                <th className={`${reportTh} text-right`}>raw crossings</th>
                <th className={reportTh}>SoC in→out</th>
                <th className={reportTh}>round trip</th>
              </tr>
            </thead>
            <tbody>
              {loading && cleanRows == null ? <SkeletonTagRows cols={10} /> : null}
              {(cleanRows ?? []).map((r, i) => {
                const anim = rowEntrance(i)
                return (
                  <tr
                    key={`${r.vehicle_id}:${r.geofence_id}:${r.ts_in}`}
                    className={`${reportTr} ${anim.className}`}
                    style={{ animationDelay: anim.delay }}
                  >
                    <td className={`${reportTd} font-mono text-slate-600`}>
                      {new Date(r.ts_in).toLocaleString('en-IN')}
                    </td>
                    <td className={`${reportTd} font-mono text-slate-600`}>
                      {new Date(r.ts_out).toLocaleString('en-IN')}
                    </td>
                    <td className={`${reportTd} font-mono font-medium text-slate-800`}>{r.vehicle_id}</td>
                    <td className={reportTd}>{r.site ?? '—'}</td>
                    <td className="px-3 py-2">{r.geofence_name}</td>
                    <td className={`${reportTd} text-slate-400`}>{r.fence_type ?? '—'}</td>
                    <td className={`${reportTd} text-right font-mono`}>
                      {r.dwell_sec != null ? `${Math.round(r.dwell_sec / 60)}m` : '—'}
                    </td>
                    <td className={`${reportTd} text-right font-mono text-slate-400`}>
                      {r.raw_crossings}
                    </td>
                    <td className={`${reportTd} font-mono`}>
                      {r.soc_in ?? '—'}→{r.soc_out ?? '—'}
                    </td>
                    <td className={`${reportTd} font-mono text-slate-400`}>
                      {r.round_trip_id ?? '—'}
                    </td>
                  </tr>
                )
              })}
              {cleanRows && cleanRows.length === 0 ? (
                <EmptyTableRow cols={10}>No geo tags in this window</EmptyTableRow>
              ) : null}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-left text-[11.5px]" aria-busy={loading || undefined}>
            <thead className={reportThead}>
              <tr>
                <th className={reportTh}>time</th>
                <th className={reportTh}>vehicle</th>
                <th className={reportTh}>site</th>
                <th className={reportTh}>event</th>
                <th className={reportTh}>geofence</th>
                <th className={reportTh}>type</th>
                <th className={reportTh}>hub</th>
                <th className={`${reportTh} text-right`}>dwell</th>
                <th className={reportTh}>SoC in→out</th>
                <th className={reportTh}>round trip</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows == null ? <SkeletonTagRows cols={10} /> : null}
              {(rows ?? []).map((r, i) => {
                const anim = rowEntrance(i)
                return (
                  <tr
                    key={`${r.vehicle_id}:${r.geofence_id}:${r.ts}:${r.event}`}
                    className={`${reportTr} ${anim.className}`}
                    style={{ animationDelay: anim.delay }}
                  >
                    <td className={`${reportTd} font-mono text-slate-600`}>
                      {new Date(r.ts).toLocaleString('en-IN')}
                    </td>
                    <td className={`${reportTd} font-mono font-medium text-slate-800`}>{r.vehicle_id}</td>
                    <td className={reportTd}>{r.site ?? '—'}</td>
                    <td className={reportTd}>{eventPill(r.event)}</td>
                    <td className="px-3 py-2">{r.geofence_name}</td>
                    <td className={`${reportTd} text-slate-400`}>{r.fence_type ?? '—'}</td>
                    <td className={reportTd}>{r.hub_code ?? '—'}</td>
                    <td className={`${reportTd} text-right font-mono`}>
                      {r.dwell_sec != null ? `${Math.round(r.dwell_sec / 60)}m` : '—'}
                    </td>
                    <td className={`${reportTd} font-mono`}>
                      {r.soc_in ?? '—'}→{r.soc_out ?? '—'}
                    </td>
                    <td className={`${reportTd} font-mono text-slate-400`}>
                      {r.round_trip_id ?? '—'}
                    </td>
                  </tr>
                )
              })}
              {rows && rows.length === 0 ? (
                <EmptyTableRow cols={10}>No geo tags in this window</EmptyTableRow>
              ) : null}
            </tbody>
          </table>
        )}
        </div>
      </div>
    </div>
  )
}
